import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { 
  Sparkles, TrendingUp, Target, Zap, Shield, Clock, 
  Users, Database, BarChart3, CheckCircle2, ArrowRight,
  Globe, Rocket, Award
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_467e7424-249d-4984-8afc-452f42a05c3c/artifacts/6ivxke97_Logo.png';

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const features = [
    { icon: Rocket, title: 'Lightning Fast', desc: 'Process thousands of records in seconds' },
    { icon: Shield, title: 'Bank-Grade Security', desc: 'Enterprise-level data protection' },
    { icon: BarChart3, title: 'Real-time Analytics', desc: 'Live dashboards and insights' },
    { icon: Globe, title: 'Access Anywhere', desc: 'Cloud-based for remote teams' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email,
        password,
      });

      const { access_token, user } = response.data;
      onLogin(user, access_token);
      toast.success('Welcome back! 🎉');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden bg-slate-900">
      {/* Animated Gradient Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900/20 to-slate-900"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-blue-600/20 via-transparent to-transparent"></div>
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-emerald-600/20 via-transparent to-transparent"></div>
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-white/10 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 10}s`,
            }}
          />
        ))}
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>

      <div className="relative z-10 flex flex-1">
        {/* Left Side - Premium Branding & Features */}
        <div className="hidden lg:flex lg:w-3/5 flex-col justify-between p-12 text-white">
          {/* Top Section - Logo & Title */}
          <div>
            <div className="flex items-center gap-4 mb-16">
              <img 
                src={LOGO_URL} 
                alt="Creative Infotech" 
                className="w-16 h-16 object-contain drop-shadow-2xl"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                  Creative Infotech
                </h1>
                <p className="text-sm text-slate-400">Enterprise CRM Platform</p>
              </div>
            </div>

            {/* Hero Section */}
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500/20 to-emerald-500/20 rounded-full border border-white/10 mb-6">
                <Award className="w-4 h-4 text-yellow-400" />
                <span className="text-sm text-slate-300">Trusted by Industry Leaders</span>
              </div>
              
              <h2 className="text-5xl font-bold mb-6 leading-tight">
                <span className="bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent">
                  Transform Your Sales
                </span>
                <br />
                <span className="bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">
                  Into Success Stories
                </span>
              </h2>
              
              <p className="text-xl text-slate-400 mb-12 leading-relaxed">
                The most powerful CRM platform designed for high-performance sales teams. 
                Manage leads, track conversions, and scale your business effortlessly.
              </p>

              {/* Feature Grid */}
              <div className="grid grid-cols-2 gap-4 mb-12">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div 
                      key={index} 
                      className="group p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-gradient-to-br from-blue-500/20 to-emerald-500/20 rounded-lg group-hover:scale-110 transition-transform">
                          <Icon className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-white mb-1">{feature.title}</h3>
                          <p className="text-sm text-slate-400">{feature.desc}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Trust Badge */}
          <div className="flex items-center gap-3 p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
            <Sparkles className="w-5 h-5 text-yellow-400" />
            <p className="text-sm text-slate-300">
              <span className="font-semibold text-white">Trusted by professionals</span> to manage thousands of sales applications efficiently
            </p>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex-1 flex items-center justify-center p-8 lg:bg-slate-950/50 lg:backdrop-blur-xl">
          <Card className="w-full max-w-md shadow-2xl border-0 bg-white overflow-hidden" data-testid="login-card">
            {/* Gradient Top Bar */}
            <div className="h-1.5 bg-gradient-to-r from-blue-500 via-emerald-500 to-blue-500"></div>
            
            <CardContent className="p-8 lg:p-10">
              {/* Mobile Logo */}
              <div className="lg:hidden mb-8 text-center">
                <img 
                  src={LOGO_URL} 
                  alt="Creative Infotech" 
                  className="w-16 h-16 object-contain mx-auto mb-4"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">
                  Creative Infotech
                </h2>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-2">Welcome Back</h2>
                <p className="text-slate-500">Sign in to access your dashboard</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                    data-testid="email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                    Password
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                    data-testid="password-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-base font-semibold bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700 shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transform hover:-translate-y-0.5 transition-all duration-200"
                  disabled={loading}
                  data-testid="login-button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Signing in...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Sign In
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </form>

              {/* Security Badge */}
              <div className="mt-8 pt-6 border-t border-slate-100">
                <div className="flex items-center justify-center gap-6 text-xs text-slate-400">
                  <div className="flex items-center gap-1.5">
                    <Shield className="w-4 h-4 text-emerald-500" />
                    <span>SSL Secured</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    <span>JWT Auth</span>
                  </div>
                </div>
              </div>

              {/* Quick Access Info */}
              <div className="mt-6 p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-medium text-slate-700">Quick Start</span>
                </div>
                <p className="text-xs text-slate-500">
                  New to the platform? Contact your administrator to get your login credentials.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="absolute bottom-0 left-0 right-0 py-4 text-center">
        <p className="text-slate-400 text-sm">
          <span className="font-semibold text-slate-300">Creative Infotech</span>
          <span className="mx-2 text-slate-600">|</span>
          <span>2024</span>
          <span className="mx-2 text-slate-600">|</span>
          <span>All rights reserved</span>
        </p>
      </footer>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.3; }
          50% { transform: translateY(-20px) rotate(180deg); opacity: 0.6; }
        }
        .animate-float {
          animation: float 10s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Login;
